import { useState } from 'react';
import { FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import theme from '@/theme';
import { PageSection } from '@/components/Layout/PageLayout';
import {
  readHandoffPreference,
  writeHandoffPreference,
  type HandoffPreference,
} from '@/services/desktopNoteHandoffService';

const OPTIONS: { value: HandoffPreference; label: string }[] = [
  { value: 'ask', label: 'Ask me each time' },
  { value: 'always', label: 'Always open in the desktop app' },
  { value: 'never', label: 'Always stay in the browser' },
];

/**
 * The per-browser note handoff preference.
 *
 * This is the only way back from "never" short of clearing site data, so it
 * renders on this page whether or not there is a build to download.
 */
const DesktopHandoffPreference = () => {
  const [preference, setPreference] = useState<HandoffPreference>(readHandoffPreference);

  const handleChange = (_event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    const next = value as HandoffPreference;
    setPreference(next);
    writeHandoffPreference(next);
  };

  return (
    <PageSection title="Opening notes">
      <Stack gap="0.5rem" alignItems="flex-start">
        <Typography variant="body2" color={theme.palette.neutral.greyFont}>
          What this browser does with a note link when this computer has the desktop app.
        </Typography>

        <RadioGroup
          aria-label="Opening notes"
          name="desktop-handoff-preference"
          value={preference}
          onChange={handleChange}
        >
          {OPTIONS.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio size="small" />}
              label={<Typography variant="body2">{option.label}</Typography>}
              sx={{ ml: 0, gap: '0.5rem' }}
            />
          ))}
        </RadioGroup>
      </Stack>
    </PageSection>
  );
};

export default DesktopHandoffPreference;
